import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LysepAPI } from '../../contexts/ApiContext';
import { usePermissions } from '../../contexts/PermissionContext';
import ServerDetail from './ServerDetail';
import ServerList from './components/ServerList';
import ServerManagementModal from './components/ServerManagementModal';
import { Server } from 'lucide-react';

const ServerListPage = () => {
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const [servers, setServers] = useState([]);
    const [credentials, setCredentials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingServer, setEditingServer] = useState(null);

    const fetchServers = async () => {
        setLoading(true);
        try {
            const resp = await LysepAPI.get('/api/servers');
            if (resp.data.state) {
                setServers(resp.data.data);
            } else {
                toast.error(resp.data.message);
            }
        } catch (error) {
            console.error('Fetch servers error:', error);
            toast.error('Sunucular yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const fetchCredentials = async () => {
        try {
            const resp = await LysepAPI.get('/api/credentials');
            if (resp.data.state) {
                setCredentials(resp.data.data);
            }
        } catch (error) {
            console.error("Error fetching credentials:", error);
        }
    };

    useEffect(() => {
        fetchServers();
        fetchCredentials();
    }, []);

    const handleCreate = () => {
        setEditingServer(null);
        setIsModalOpen(true);
    };

    const handleEdit = (server) => {
        setEditingServer(server);
        setIsModalOpen(true);
    };

    const handleDelete = async (server) => {
        if (window.confirm(`${server.name} sunucusunu silmek istediğinizden emin misiniz?`)) {
            try {
                const resp = await LysepAPI.delete(`/api/servers/${server.id}`);
                if (resp.data.state) {
                    toast.success('Sunucu silindi');
                    fetchServers();
                } else {
                    toast.error(resp.data.message);
                }
            } catch (error) {
                toast.error('Sunucu silinirken hata oluştu');
            }
        }
    };

    const handleSave = async (formData) => {
        try {
            let resp;
            if (formData.id) {
                resp = await LysepAPI.put(`/api/servers/${formData.id}`, formData);
            } else {
                resp = await LysepAPI.post('/api/servers', formData);
            }

            if (resp.data.state) {
                toast.success(formData.id ? 'Sunucu güncellendi' : 'Sunucu oluşturuldu');
                setIsModalOpen(false);
                fetchServers();
            } else {
                toast.error(resp.data.message);
            }
        } catch (error) {
            console.error('Save server error:', error);
            toast.error(error.response?.data?.message || 'Sunucu kaydedilirken hata oluştu');
        }
    };

    const handleTestConnection = async (server) => {
        try {
            const resp = await LysepAPI.post(`/api/servers/${server.id}/ssh/test`);
            if (resp.data.state) {
                toast.success(`Bağlantı Başarılı: ${resp.data.message}`);
                // Update the specific server's last connection time in local state for immediate feedback
                setServers(prev => prev.map(s =>
                    s.id === server.id ? { ...s, last_connection_at: new Date().toISOString() } : s
                ));
            } else {
                toast.error(`Bağlantı Hatası: ${resp.data.message}`);
            }
        } catch (error) {
            toast.error(`Hata: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleSetMaster = async (server) => {
        try {
            const resp = await LysepAPI.put(`/api/servers/${server.id}/set-master`);
            if (resp.data.state) {
                toast.success(`${server.name} Master sunucu olarak ayarlandı`);
                fetchServers();
            } else {
                toast.error(resp.data.message);
            }
        } catch (error) {
            toast.error('Master sunucu ayarlanırken hata oluştu');
        }
    };

    const handleView = (server) => {
        navigate(`${server.id}`);
    };

    return (
        <div className="p-6 space-y-6 animate-fadeIn h-[calc(100vh-80px)] flex flex-col">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Server className="w-8 h-8 text-blue-500" />
                        Sunucu Yönetimi
                    </h1>
                    <p className="text-gray-400 mt-1">Sunucularınızı ve bağlantılarını yönetin</p>
                </div>

                <button
                    onClick={handleCreate}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-lg shadow-blue-500/20"
                >
                    <Server className="w-4 h-4" />
                    Yeni Sunucu Ekle
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0">
                <ServerList
                    servers={servers}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onView={handleView}
                    onTestConnection={handleTestConnection}
                    onSetMaster={handleSetMaster}
                />
            </div>

            <ServerManagementModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                server={editingServer}
                onSave={handleSave}
                credentials={credentials}
            />
        </div>
    );
};

const ServersModule = () => {
    return (
        <Routes>
            <Route path="/" element={<ServerListPage />} />
            <Route path="/:id/*" element={<ServerDetail />} />
        </Routes>
    );
};

export default ServersModule;
